class TestimonialCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.track = this.querySelector('[data-carousel-track]');
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.liveRegion = this.querySelector('[data-carousel-live]');

    // Carousel only earns its chrome at 3+ testimonials. With 1 or 2 the
    // markup stays in its static, centered fallback layout (see CSS).
    if (this.slides.length < 3 || !this.track) return;

    this.currentIndex = 0;
    this.total = this.slides.length;
    this.active = false;

    this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._desktopQuery = window.matchMedia('(min-width: 768px)');

    this._onBreakpoint = () => {
      if (this.active) this._render();
    };
    this._desktopQuery.addEventListener('change', this._onBreakpoint);

    this.prevBtn?.addEventListener('click', this._onPrev);
    this.nextBtn?.addEventListener('click', this._onNext);
    this.addEventListener('keydown', this._onKeydown);
    this._onResize = () => {
      if (this.active) this._render();
    };
    window.addEventListener('resize', this._onResize);
    this._setupSwipe();

    this._activate();
  }

  disconnectedCallback() {
    this._desktopQuery?.removeEventListener('change', this._onBreakpoint);
    this.prevBtn?.removeEventListener('click', this._onPrev);
    this.nextBtn?.removeEventListener('click', this._onNext);
    this.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onResize);
    this._teardownSwipe();
  }

  _activate() {
    this.active = true;
    this.dataset.carouselActive = '';
    this._render();
  }

  _onPrev = () => this._go(-1);
  _onNext = () => this._go(1);

  _go(direction) {
    if (!this.active) return;
    this.currentIndex = (this.currentIndex + direction + this.total) % this.total;
    this._render();
    this._announce();
  }

  _onKeydown = (e) => {
    if (!this.active) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._go(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._go(1);
    }
  };

  _render() {
    const isDesktop = this._desktopQuery.matches;
    const gap = 36;
    const sideScale = 0.85;
    const trackW = this.track.offsetWidth || 1200;
    const centerX = trackW / 2;

    this.slides.forEach((slide, i) => {
      let offset = (((i - this.currentIndex) % this.total) + this.total) % this.total;
      if (offset > this.total / 2) offset -= this.total;

      const cardW = slide.offsetWidth || 520;
      const halfW = cardW / 2;

      let left, scale, opacity, zIndex, hidden;

      if (offset === 0) {
        left = centerX - halfW;
        scale = 1;
        opacity = 1;
        zIndex = 3;
        hidden = false;
      } else if (Math.abs(offset) === 1 && isDesktop) {
        const sideVisualHalfW = halfW * sideScale;
        const sideSpacing = halfW + gap + sideVisualHalfW;
        const visualCenter = centerX + offset * sideSpacing;
        left = visualCenter - halfW;
        scale = sideScale;
        opacity = 1;
        zIndex = 2;
        hidden = true;
      } else {
        left = offset > 0 ? trackW : -cardW;
        scale = isDesktop ? 0.7 : 1;
        opacity = 0;
        zIndex = 1;
        hidden = true;
      }

      slide.style.left = `${left}px`;
      slide.style.top = '50%';
      slide.style.transform = `translateY(-50%) scale(${scale})`;
      slide.style.opacity = String(opacity);
      slide.style.zIndex = String(zIndex);
      slide.setAttribute('aria-hidden', String(hidden));
    });
  }

  _announce() {
    if (!this.liveRegion) return;
    const template = this.dataset.slideLabel || 'Testimonial __current__ of __total__';
    this.liveRegion.textContent = template
      .replace('__current__', String(this.currentIndex + 1))
      .replace('__total__', String(this.total));
  }

  _setupSwipe() {
    this._startX = 0;
    this._tracking = false;
    this._onPointerDown = (e) => {
      if (!this.active || e.pointerType === 'mouse') return;
      this._startX = e.clientX;
      this._tracking = true;
    };
    this._onPointerUp = (e) => {
      if (!this._tracking) return;
      this._tracking = false;
      const dx = e.clientX - this._startX;
      if (dx > 50) this._go(-1);
      else if (dx < -50) this._go(1);
    };
    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('pointerup', this._onPointerUp);
    this.addEventListener('pointercancel', this._onPointerUp);
  }

  _teardownSwipe() {
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointerup', this._onPointerUp);
    this.removeEventListener('pointercancel', this._onPointerUp);
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
